import { CSRF_HEADER, type OauthConsent } from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { PermissionList, writeConfirmed } from "./Permissions.tsx";
import { StandaloneState } from "./SignIn.tsx";
import { signInNavigation } from "./session-ended.ts";

/**
 * A native client's consent screen (openspec change native-auth-server, design D2): which
 * application, where it sends the browser back, what it may use, for which organisation and
 * account. Shown on every authorization; nothing is remembered. Allow or Deny ends on the
 * client's redirect, through the authorization server.
 */
export function ConsentPage({ uid, csrfToken }: { uid: string; csrfToken: string }) {
  const { t } = useTranslation();
  useDocumentTitle(t("connect.documentTitle"));
  const [busy, setBusy] = useState<"confirm" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const details = useQuery({
    queryKey: ["oauth-consent", uid],
    queryFn: async () =>
      ok(await api.api.oauth.interaction[":uid"].details.$get({ param: { uid } })),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  async function decide(choice: "confirm" | "deny") {
    setBusy(choice);
    setError(null);
    try {
      const interaction = api.api.oauth.interaction[":uid"];
      const headers = { headers: { [CSRF_HEADER]: csrfToken } };
      // `write` only when this screen named it (mcp-write-tools D2).
      const write = writeConfirmed(details.data?.scopes);
      const { redirectTo } =
        choice === "confirm"
          ? await ok(await interaction.confirm.$post({ param: { uid }, json: { write } }, headers))
          : await ok(await interaction.deny.$post({ param: { uid } }, headers));
      signInNavigation.go(redirectTo);
    } catch (e) {
      setError(refusal(e));
      setBusy(null);
    }
  }

  function refusal(e: unknown): string {
    if (e instanceof ApiError && e.status === 404) {
      return t("connect.errors.expired");
    }
    if (e instanceof ApiError && e.status === 403) {
      return t("connect.errors.otherSession");
    }
    return e instanceof ApiError ? e.message : t("errors.network");
  }

  if (details.isPending) {
    return <StandaloneState title={t("connect.loading")}>{null}</StandaloneState>;
  }
  if (details.isError) {
    return (
      <StandaloneState title={t("connect.errors.title")}>
        <Notice tone="danger">{refusal(details.error)}</Notice>
      </StandaloneState>
    );
  }
  const consent: OauthConsent = details.data;
  const user = consent.user.displayName
    ? `${consent.user.displayName} (${consent.user.email})`
    : consent.user.email;
  return (
    <StandaloneState title={t("connect.title", { client: consent.client.name })}>
      <p className="text-sm leading-relaxed text-fg-secondary">{t("connect.lead")}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-fg-secondary">{t("connect.client")}</dt>
        <dd className="min-w-0">
          <span className="font-medium text-fg">{consent.client.name}</span>
          {/* The client ID's domain, never its logo: no third-party fetch from the browser. */}
          <span className="block break-all text-xs text-fg-muted">
            {consent.client.host
              ? t("connect.hostHint", { host: consent.client.host })
              : t("connect.firstParty")}
          </span>
        </dd>
        <dt className="text-fg-secondary">{t("connect.redirect")}</dt>
        <dd className="font-mono text-fg">{consent.redirectHost}</dd>
        <dt className="text-fg-secondary">{t("connect.resource")}</dt>
        <dd className="min-w-0">
          <span className="text-fg">{t(`connect.resources.${consent.resource}`)}</span>
          {consent.resource === "api" ? (
            <span className="block text-xs text-fg-muted">{t("connect.resourceHints.api")}</span>
          ) : null}
        </dd>
        <dt className="text-fg-secondary">{t("connect.permissions.label")}</dt>
        <dd className="min-w-0">
          <PermissionList scopes={consent.scopes} resource={consent.resource} />
        </dd>
        <dt className="text-fg-secondary">{t("connect.tenant")}</dt>
        <dd className="text-fg">{consent.tenant.name}</dd>
        <dt className="text-fg-secondary">{t("connect.user")}</dt>
        <dd className="break-words text-fg">{user}</dd>
      </dl>
      <Notice tone="warning">{t("connect.warning")}</Notice>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="flex gap-2">
        <Button disabled={busy !== null} onClick={() => void decide("confirm")}>
          {busy === "confirm" ? t("connect.allowing") : t("connect.allow")}
        </Button>
        <Button variant="outline" disabled={busy !== null} onClick={() => void decide("deny")}>
          {t("connect.deny")}
        </Button>
      </div>
    </StandaloneState>
  );
}
